import preventDefault
  from '@mailobj-browser/front/js/events/hooks/preventDefault.js'
import stopImmediatePropagation
  from '@mailobj-browser/front/js/events/hooks/stopImmediatePropagation.js'
import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import capture from '@mailobj-browser/front/js/events/options/capture.js'
import once from '@mailobj-browser/front/js/events/options/once.js'
import passive from '@mailobj-browser/front/js/events/options/passive.js'
import focusType from '@mailobj-browser/front/js/events/types/focus.js'
import keyDown from '@mailobj-browser/front/js/events/types/keyDown.js'
import keyUp from '@mailobj-browser/front/js/events/types/keyUp.js'
import scroll from '@mailobj-browser/front/js/events/types/scroll.js'
import submit from '@mailobj-browser/front/js/events/types/submit.js'
import all from '@mailobj-browser/front/js/selectors/all.js'
import one from '@mailobj-browser/front/js/selectors/one.js'
import append from '@mailobj-browser/front/js/tree/append.js'
import remove from '@mailobj-browser/front/js/tree/remove.js'
import removed from '@mailobj-browser/front/js/tree/removed.js'
import replaceChildren from '@mailobj-browser/front/js/tree/replaceChildren.js'
import array from '@mailobj-browser/front/js/utils/array.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import wait from '@mailobj-browser/front/js/utils/wait.js'
import { fromEvent, fromNode, move, rect, resize } from '../fixed/fixed.js'
import * as utilities from '../styles.js'
import { render } from '../template/template.js'
import { modifiers } from './styles.js'
import * as sharedClasses from '#utilities/js/ea/sharedClasses.js'
import globals from '#utilities/js/ea/globals.js'

let current = null
let menu = null
let tabulating = false
let stashed = []
let initial = null
let closePrevented = false

const openers = new WeakMap()

export const events = object(null, {
  close: 'lightbox-close',
  open: 'lightbox-open'
})

const focusable = `
  a[href]:not([hidden]):not([aria-hidden="true"]),
  button:not([disabled]):not([hidden]):not([aria-hidden="true"]),
  input:not([type="hidden"]):not([disabled]):not([hidden]):not([aria-hidden="true"]),
  textarea:not([disabled]):not([hidden]):not([aria-hidden="true"]),
  select:not([disabled]):not([hidden]):not([aria-hidden="true"]),
  details:not([hidden]):not([aria-hidden="true"]),
  [tabindex]:not([tabindex="-1"]):not([disabled]):not([hidden]):not([aria-hidden="true"])
`

const focusing = `
  a:not([data-contract="modal.close"]),
  button:not(:disabled),
  label:not(:disabled),
  summary
`

const focusables = (dialog, reversed) => {
  const elements = all(focusable, dialog).filter(visible)

  return reversed ?
    elements.toReversed() :
    elements
}

const visible = element => {
  const { height, width } = rect(element)

  return height && width
}

const opening = lightbox => {
  const opener = openers.get(lightbox)

  if (!opener) {
    return
  }

  opener.classList.add(modifiers.lightbox.open)
  if (opener.hasAttribute('aria-expanded')) {
    opener.setAttribute('aria-expanded', 'true')
  }

  if (typeof globals.ea_timeout_refresh_pause === 'function') {
    globals.ea_timeout_refresh_pause()
  }

  queueMicrotask(async () => {
    await removed(lightbox)
    opener.classList.remove(modifiers.lightbox.open)
    if (opener.hasAttribute('aria-expanded')) {
      opener.setAttribute('aria-expanded', 'false')
    }
  })
}

export const stash = () => {
  if (menu) {
    stashed.length = 0
    stashed.push(menu, menu.parentNode)
    menu.remove()
  }
}

export const unstash = () => {
  if (stashed.length) {
    menu = stashed[0]
    stashed[1].append(menu)
    opening(menu)
    stashed.length = 0
  }
}

export const preventClose = () => {
  const current = menu

  if (current && current.ownerDocument.contains(current)) {
    closePrevented = true
  }
}

export const isClosePrevented = () => {
  return closePrevented
}

export const releaseClosePrevented = () => {
  closePrevented = false
}

export const close = () => {
  if (current) {
    if (typeof globals.ea_timeout_refresh_unpause === 'function') {
      globals.ea_timeout_refresh_unpause()
    }

    if (closePrevented) {
      releaseClosePrevented()
      return
    }

    const { ownerDocument } = current
    const { body, defaultView } = ownerDocument
    const { CustomEvent } = defaultView
    const opener = openers.get(current)

    sharedClasses.remove(current, utilities.modifiers.overflow.hidden)
    onFocus.forget(body)
    remove(current)
    current = null

    opener?.dispatchEvent(new CustomEvent(events.close, {
      bubbles: true
    }))
  }
}

export const focus = (
  item = null
) => {
  if (item) {
    one(focusing, item)?.focus()
  }
}

export const hasModal = () => {
  return current !== null &&
    current !== menu &&
    current.ownerDocument.contains(current)
}

const onScroll = object(listener, {
  type: scroll,
  once,
  passive,
  task: close
})

export const onTab = object(listener, {
  type: keyDown,
  passive,
  task: (document, event) => {
    const { key } = event

    tabulating = key === 'Tab'
  }
})

export const onEscape = object(listener, {
  type: keyUp,
  task: (document, event) => {
    const { key } = event

    tabulating = false

    if (key !== 'Escape') {
      return
    }

    if (menu && document.contains(menu)) {
      preventDefault(event)
      stopImmediatePropagation(event)
      remove(menu)
      menu = null

      return
    }

    if (current) {
      preventDefault(event)
      stopImmediatePropagation(event)
      close()
    }
  }
})

const onFocus = object(listener, {
  type: focusType,
  capture,
  passive,
  task: (body, event) => {
    if (!tabulating) {
      return
    }

    const dialog = one('dialog', current)
    const { relatedTarget, target } = event
    const { ownerDocument } = body
    const { documentElement } = ownerDocument

    if (target === dialog) {
      documentElement.focus()
    } else if (relatedTarget === documentElement) {
      const [first] = all(focusable, dialog).filter(focusables)

      first?.focus()
    }
  }
})

const onSubmit = object(listener, {
  type: submit,
  hooks: array([
    preventDefault
  ]),
  task: () => {}
})

export const display = (content, opener, event = null) => {
  const { ownerDocument } = opener

  close()
  move(content)

  requestAnimationFrame(() => {
    if (event) {
      move(content, fromEvent(content, event))
    } else {
      resize(content, opener)
      move(content, fromNode(content, opener))
    }

    focus(content)
    onScroll.listen(ownerDocument)

    current = content
  })
}

export const parse = async (
  template,
  container,
  opener = null,
  asModal = false
) => {
  const { ownerDocument } = template
  const { defaultView } = ownerDocument
  const { CustomEvent } = defaultView
  const lightbox = await render(template, true)

  if (opener && !container.contains(opener)) {
    initial = opener
  }

  openers.set(lightbox, opener)
  opening(lightbox)

  if (asModal) {
    if (menu) {
      remove(menu)
    }

    const { body } = ownerDocument

    sharedClasses.add(lightbox, utilities.modifiers.overflow.hidden)
    onFocus.listen(body)
    onTab.listen(ownerDocument)
    onEscape.listen(ownerDocument)
    current = lightbox
    replaceChildren(container, lightbox)
  } else {
    menu = lightbox
    append(container, lightbox)
  }

  for (const form of all('form[target="_self"]')) {
    onSubmit.listen(form)
  }

  opener.dispatchEvent(new CustomEvent(events.open, {
    bubbles: true,
    detail: lightbox
  }))

  queueMicrotask(async () => {
    await removed(lightbox)

    if (asModal) {
      await wait(150)
      initial?.focus()
      initial = null
    }
  })

  if (asModal) {
    queueMicrotask(async () => {
      await removed(initial ?? opener)
      close()
      remove(lightbox)
    })
  }

  return lightbox
}

export const open = async (
  template,
  container,
  opener = null,
  asModal = false
) => {
  const lightbox = await parse(template, container, opener, asModal)

  queueMicrotask(async () => {
    await removed(opener)

    if (lightbox === current) {
      close()
    }
  })

  return lightbox
}

export const opener = lightbox => openers.get(lightbox)
